import { api } from './api';

export const ADMINISTRATIVE_ROLES = ['CLINIC_ADMIN'];
export const requireAccountAdmin = (actor) => {
    if (!actor?.user?.uuid || actor.role?.name !== 'ACCOUNT_ADMIN' || actor.role.disabled
        || !actor.role.uuid || !actor.role.account?.uuid) throw new Error('ADMINISTRATIVES_ACCESS_DENIED');
    return { 'CHEART-ROLE': actor.role.uuid };
};
const verifyTarget = (actor, target) => {
    requireAccountAdmin(actor);
    if (!target?.role_uuid || !ADMINISTRATIVE_ROLES.includes(target.role)
        || target.account?.uuid !== actor.role.account.uuid) throw new Error('INVALID_ADMINISTRATIVE_TARGET');
};
export const getAdministratives = async (actor, {
    disabled = false, search = '', token = null, pageSize = 10, signal
} = {}) => {
    const headers = requireAccountAdmin(actor);
    Object.assign(headers, {
        'CHEART-QUERY-ROLE': JSON.stringify(ADMINISTRATIVE_ROLES), 'CHEART-QUERY-DISABLED': String(disabled),
        'PAGINATION-LIMIT': pageSize
    });
    if (token) headers['PAGINATION-TOKEN'] = token;
    const query = search.trim();
    // The backend scopes clinic actors to their selected account. Do not send an account override.
    const { data, headers: pagination } = query
        ? await api.post('/cheart/users/search', { query }, { headers, signal })
        : await api.get('/cheart/users', { headers, signal });
    const total = Number(pagination['pagination-count']);
    if (!Array.isArray(data) || !Number.isInteger(total) || total < 0) throw new Error('INVALID_ACCESS_PAGE');
    for (const target of data) {
        verifyTarget(actor, target);
        if (!ADMINISTRATIVE_ROLES.includes(target.role) || Boolean(target.disabled) !== disabled) throw new Error('INVALID_ACCESS_PAGE');
    }
    return { rows: data, total, nextToken: pagination['pagination-token'] || null };
};

export const fetchAdministrative = async (actor, target) => {
    verifyTarget(actor, target);
    const { data } = await api.post('/cheart/users/fetch', { role_uuid: target.role_uuid }, {
        headers: requireAccountAdmin(actor)
    });
    verifyTarget(actor, data);
    if (data.role_uuid !== target.role_uuid) throw new Error('INVALID_ADMINISTRATIVE_TARGET');
    return { name: data.name, phone: data.phone || '' };
};

const findExistingAccess = async (actor, values) => {
    let token = null;
    const seen = new Set();
    do {
        const page = await getAdministratives(actor, {
            search: values.email, token, pageSize: 50
        });
        const found = page.rows.find((row) => row.email?.trim().toLowerCase() === values.email.trim().toLowerCase());
        if (found) return found;
        if (seen.size * 50 + page.rows.length >= page.total) return null;
        token = page.nextToken;
        if (!token || seen.has(token)) throw new Error('INVALID_ACCESS_PAGE');
        seen.add(token);
    } while (token);
    return null;
};

export const createAdministrative = async (actor, values) => {
    const headers = requireAccountAdmin(actor);
    const existing = await findExistingAccess(actor, values);
    if (existing) return { access: existing, recovered: true };
    try {
        const { data } = await api.post('/cheart/users', {
            name: values.name.trim(), email: values.email.trim(), role: 'CLINIC_ADMIN',
            ...(values.phone?.trim() ? { phone: values.phone.trim() } : {})
        }, { headers });
        verifyTarget(actor, data);
        if (data.role !== 'CLINIC_ADMIN' || data.disabled) throw new Error('INVALID_ADMINISTRATIVE_TARGET');
        return { access: data, recovered: false };
    } catch (error) {
        // Creating a role commits before sending the notification email.
        const confirmed = await findExistingAccess(actor, values).catch(() => null);
        if (confirmed) return { access: confirmed, recovered: true };
        throw error;
    }
};

export const updateAdministrative = async (actor, target, values) => {
    verifyTarget(actor, target);
    return api.patch('/cheart/users', {
        role_uuid: target.role_uuid, name: values.name.trim(), phone: values.phone.trim()
    }, { headers: requireAccountAdmin(actor) });
};

export const disableAdministrative = async (actor, target) => {
    verifyTarget(actor, target);
    if (target.user_uuid === actor.user.uuid) throw new Error('CANNOT_DISABLE_SELF');
    return api.delete('/cheart/users', {
        data: { role_uuid: target.role_uuid }, headers: requireAccountAdmin(actor)
    });
};
